/**
 * Memory import — shared contracts.
 *
 * Parsers turn another platform's memory files (or a pasted memory dump) into
 * MemoryImportItem[]: atomic, short, single-line facts ready for the Nami
 * observations rail. Parsing runs client-side (browser) so exports never leave
 * the user's machine; the server only ever sees confirmed items.
 * Spec: docs/superpowers/specs/2026-08-08-memory-import-design.md
 */
import type { NamiEpistemic } from '../types/nami.js';

export const MEMORY_IMPORT_PLATFORMS = [
  'chatgpt',
  'claude',
  'codex',
  'gemini',
  'cursor',
  'openclaw',
  'hermes',
  'other',
] as const;

export type MemoryImportPlatform = (typeof MEMORY_IMPORT_PLATFORMS)[number];

export function isMemoryImportPlatform(x: unknown): x is MemoryImportPlatform {
  return typeof x === 'string' && (MEMORY_IMPORT_PLATFORMS as readonly string[]).includes(x);
}

/** A candidate file already extracted to text. `name` keeps the relative path — detection reads it. */
export interface NamedText {
  name: string;
  text: string;
}

export interface MemoryImportItem {
  /** Single-line fact, ≤ IMPORT_ITEM_MAX_CHARS after chunking. */
  text: string;
  /** Parser's deterministic best guess; server defaults missing/invalid to 'other'. */
  epistemic?: NamiEpistemic;
  /** ISO timestamp only when the source carries a real date (e.g. dated daily files). */
  observedAt?: string;
  /** Where this came from, for the review step ("MEMORY.md · Preferences"). Not stored. */
  sourceDetail?: string;
}

export interface MemoryImportParseResult {
  platform: MemoryImportPlatform;
  /** True when signature detection identified the format (and possibly re-labeled). */
  detected: boolean;
  items: MemoryImportItem[];
  warnings: string[];
}

/** Client-side chunk target — imported rows must stay inside organic observation norms. */
export const IMPORT_ITEM_MAX_CHARS = 280;
/** Server-side hard cap; anything longer is truncated at deposit. */
export const IMPORT_ITEM_HARD_CHARS = 500;
/** Max items per import call; orchestrator keeps the newest and warns. */
export const IMPORT_MAX_ITEMS = 2000;
/** Per-file text ceiling for candidate extraction. */
export const IMPORT_FILE_MAX_BYTES = 1_000_000;
/** Max candidate files considered per import. */
export const IMPORT_MAX_FILES = 400;
